import { Button } from "web-design-system";
import { FileUpload } from "web-core/components";
import { useClient, useMandate, useUploadMandate } from "creso-core/hooks";
import { useState } from "react";
import { handleError } from "@/utils/error";
import { useRouter, useSearch } from "@tanstack/react-router";
import { Route } from "@/routes/__root";
import { Loader2 } from "lucide-react";
import omit from "lodash/omit";
import { MandateItem } from "web-core";
import MandateLayout from "./mandate-layout";

export default function UploadMandateNachForm() {
  const { client_id = 0, mandate_id } = useSearch({ from: Route.id });
  const { data: client } = useClient(client_id);

  const { data: mandate } = useMandate({
    investmentAccountGuid: client?.investment_account || "",
    id: mandate_id ?? "",
  });

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const { mutateAsync: uploadFile, isPending } = useUploadMandate();

  const router = useRouter();

  const handleUpload = async () => {
    if (!uploadedFile || !client?.investment_account || !mandate?.id) return;
    try {
      const formData = new FormData();
      formData.append("file_data", uploadedFile);
      formData.append("investment_account_guid", client.investment_account);
      formData.append("mandate_id", mandate.id.toString());
      await uploadFile(formData);
      router.navigate({
        to: ".",
        search: (prev: Record<string, unknown>) => omit(prev, ["context"]),
        replace: true,
      });
    } catch (error) {
      handleError(error, "Failed to upload NACH form");
    }
  };

  if (!mandate) return null;

  return (
    <MandateLayout
      title="Upload Physical Mandate"
      onBack={() => {
        router.navigate({
          to: ".",
          search: (prev: Record<string, unknown>) => omit(prev, ["context"]),
          replace: true,
        });
      }}
      action={
        <Button
          size="lg"
          className="w-full"
          disabled={isPending || !uploadedFile}
          onClick={handleUpload}
        >
          {isPending && <Loader2 className="animate-spin" />}
          {isPending ? "Uploading..." : "Upload"}
        </Button>
      }
    >
      <div className="flex flex-col gap-2 size-full">
        <MandateItem
          investmentAccountId={client?.investment_account ?? ""}
          mandate={mandate}
        />
        <div className="flex flex-1 pb-5 flex-col">
          <FileUpload
            acceptedTypes={[
              "application/pdf",
              "image/jpg",
              "image/jpeg",
              "image/png",
            ]}
            onChange={setUploadedFile}
          />
        </div>
      </div>
    </MandateLayout>
  );
}
